import { diagnostic } from '../../utils/diagnostics';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  useAuth,
} from '../../contexts/AuthContext';

import {
  clearParentOAuthTransaction,
  getParentOAuthReturnPath,
  readParentOAuthTransaction,
} from '../../utils/parentOAuth';

import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import AppLayout from '../../components/Layout/ParentAccountLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';

const OAuthCallback = () => {
  const navigate = useNavigate();

  const [
    searchParams,
  ] = useSearchParams();

  const {
    user,
    initializing,
    parentChecked,
    resolveParentAuthState,
    refreshParent,
    signOut,
  } = useAuth();

  const transactionRef =
    useRef(
      readParentOAuthTransaction()
    );

  const processingRef =
    useRef(false);

  const [
    retryCount,
    setRetryCount,
  ] = useState(0);

  const [
    pageState,
    setPageState,
  ] = useState('processing');

  const [
    message,
    setMessage,
  ] = useState(
    'Finishing your secure sign-in...'
  );

  const transaction =
    transactionRef.current;

  const returnPath =
    getParentOAuthReturnPath(
      transaction
    );

  const providerError =
    searchParams.get('error');

  const providerErrorCode =
    searchParams.get('error_code');

  const providerErrorDescription =
    searchParams.get(
      'error_description'
    );

  useEffect(() => {
    let active = true;

    const processCallback =
      async () => {
        /*
         * The identity provider returned an OAuth error.
         */
        if (
          providerError ||
          providerErrorCode
        ) {
          clearParentOAuthTransaction(
            transaction?.id || null
          );

          const normalizedError =
            (
              providerErrorDescription ||
              providerErrorCode ||
              providerError ||
              ''
            ).toLowerCase();

          const wasCancelled =
            normalizedError.includes(
              'access_denied'
            ) ||
            normalizedError.includes(
              'access denied'
            ) ||
            normalizedError.includes(
              'cancel'
            );

          if (!active) {
            return;
          }

          setPageState(
            wasCancelled
              ? 'cancelled'
              : 'provider_error'
          );

          setMessage(
            wasCancelled
              ? 'Sign-in was cancelled. Nothing was changed.'
              : 'The identity provider could not complete sign-in. Please try again.'
          );

          return;
        }

        /*
         * Wait for Supabase URL-session detection and the normal
         * AuthContext initialization to finish.
         */
        if (
          initializing ||
          !parentChecked
        ) {
          return;
        }

        if (processingRef.current) {
          return;
        }

        if (!user) {
          setPageState('session_error');

          setMessage(
            'We could not establish your secure parent session. Please return and try again.'
          );

          return;
        }

        processingRef.current = true;

        setPageState('processing');

        setMessage(
          'Checking your MyPiggy family account...'
        );

        try {
          const authState =
            await resolveParentAuthState();

          if (!active) {
            return;
          }

          /*
           * Identity and email failures must not continue into
           * Owner setup or Guardian acceptance.
           */
          if (
            [
              'identity_conflict',
              'email_unavailable',
              'email_unverified',
            ].includes(
              authState?.resolution
            )
          ) {
            clearParentOAuthTransaction(
              transaction?.id || null
            );

            setPageState(
              'identity_error'
            );

            setMessage(
              authState?.resolution ===
                'identity_conflict'
                ? 'This identity could not be safely connected to the existing MyPiggy account. Please sign in using the account’s existing method.'
                : 'The identity provider did not provide a verified email address that MyPiggy can use.'
            );

            try {
              await signOut();
            } catch {
              diagnostic('error', 'parent.oauth.rejected_session_cleanup_failed');
            }

            return;
          }


          /*
           * Guardian OAuth always returns to the exact invitation
           * route. The invitation RPC performs the authoritative
           * email and membership checks.
           */
          if (
            transaction?.flow ===
              'guardian_invitation' &&
            transaction?.inviteToken
          ) {
            clearParentOAuthTransaction(
              transaction.id
            );

            navigate(
              returnPath,
              {
                replace: true,
              }
            );

            return;
          }

          if (
            authState?.resolution ===
              'existing_parent'
          ) {
            await refreshParent({
              blockUI: true,
            });

            clearParentOAuthTransaction(
              transaction?.id || null
            );

            navigate(
              '/parent',
              {
                replace: true,
              }
            );

            return;
          }

          if (
            authState?.resolution ===
              'new_parent'
          ) {
            clearParentOAuthTransaction(
              transaction?.id || null
            );

            navigate(
              '/parent/setup',
              {
                replace: true,
              }
            );

            return;
          }

          /*
           * A valid invitation exists for the authenticated email,
           * but this callback does not have its invitation token.
           */
          if (
            authState?.resolution ===
              'invited_guardian'
          ) {
            clearParentOAuthTransaction(
              transaction?.id || null
            );

            setPageState(
              'invitation_required'
            );

            setMessage(
              'A Parent/Guardian invitation exists for this email. Please open the original invitation link to join the family.'
            );

            try {
              await signOut();
            } catch {
              diagnostic('error', 'parent.oauth.invitation_cleanup_failed');
            }

            return;
          }


          throw new Error(
            'The authentication response was not recognized.'
          );
        } catch {
          diagnostic('error', 'parent.oauth.callback_failed');

          if (!active) {
            return;
          }

          processingRef.current = false;

          setPageState(
            'processing_error'
          );

          setMessage(
            'We could not finish checking your MyPiggy account. Please try again.'
          );
        }
      };

    processCallback();

    return () => {
      active = false;
    };
  }, [
    user,
    initializing,
    parentChecked,
    providerError,
    providerErrorCode,
    providerErrorDescription,
    resolveParentAuthState,
    refreshParent,
    signOut,
    transaction,
    returnPath,
    navigate,
    retryCount,
  ]);

  const handleReturn =
    async () => {
      if (
        pageState ===
          'identity_error' ||
        pageState ===
          'invitation_required'
      ) {
        try {
          await signOut();
        } catch {
          diagnostic('error', 'parent.oauth.return_cleanup_failed');
        }
      }

      navigate(
        returnPath,
        {
          replace: true,
        }
      );
    };

  const handleRetry = () => {
    processingRef.current = false;

    setRetryCount(
      (current) =>
        current + 1
    );
  };

  const isProcessing =
    pageState === 'processing';

  return (
    <AppLayout title="MyPiggy">
      <div className="max-w-md mx-auto">
        <Card className="p-4 sm:p-5">
          <div className="text-center">
            <MyPiggyIcon name={isProcessing ? 'parent-approval' : 'my-piggy-bank'} size="lg" className="mb-3" />

            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-slate-100 mb-3">
              {isProcessing
                ? 'Secure sign-in'
                : 'Sign-in update'}
            </h1>

            <p
              className="text-gray-600 dark:text-slate-300 leading-relaxed"
              role={
                isProcessing
                  ? 'status'
                  : 'alert'
              }
            >
              {message}
            </p>

            {isProcessing && (
              <div
                className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700"
                aria-hidden="true"
              >
                <div className="h-full w-1/2 animate-pulse rounded-full bg-blue-600" />
              </div>
            )}

            {pageState ===
              'processing_error' && (
              <div className="mt-4 space-y-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={
                    handleRetry
                  }
                >
                  Try Again
                </Button>

                <button
                  type="button"
                  className="w-full text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-200"
                  onClick={
                    handleReturn
                  }
                >
                  Return to Sign In
                </button>
              </div>
            )}

            {!isProcessing &&
              pageState !==
                'processing_error' && (
                <Button
                  type="button"
                  className="w-full mt-4"
                  onClick={
                    handleReturn
                  }
                >
                  {transaction?.flow ===
                    'guardian_invitation'
                    ? 'Return to Invitation'
                    : 'Return to Sign In'}
                </Button>
              )}
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};

export default OAuthCallback;
